import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useOnboarding } from '../components/onboarding';
import { GuideWelcomeModal, BuildArchiveChecklist, GuideLauncher } from '../components/guide';
import { markGuideExportPreviewed } from '../utils/guideProgress';
import { ArchiveHelperText } from '../components/archive/ArchiveHelperText';
import { ArchiveTreeCover } from '../components/archive/ArchiveTreeCover';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
// Theme toggle removed - light mode only
import { toast } from 'sonner';
import { Plus, GitBranch, Clock, Users, LogOut, Settings as SettingsIcon, Trash2, Dna, HelpCircle, User, ChevronDown, ClipboardCheck, ChevronRight, Link2 } from 'lucide-react';
import { EmptyState } from '../components/ui/EmptyState';
import { LoadingState } from '../components/ui/LoadingState';
import { humanizeError } from '../utils/errorHandling';
import { getTreeCollaborationRoleLabel } from '../utils/personDisplay';
import { getApiBase } from '../utils/apiBase';
import {
  getTreeLimitUpgradeMessage,
  getTreeUsageLabel,
} from '../utils/treePlanLimits';
import AppFooter from '../components/AppFooter';
import { BrandWordmark } from '../components/brand/BrandWordmark';
import { getMyArchivePersonId, findNameMatchMyPerson, setMyArchivePersonId as clearPersistedArchivePersonIfStale } from '../utils/myArchivePerson';
import { getSelfPromptDismissed, dismissSelfPrompt } from '../utils/selfPersonLink';
import { SelfPersonPrompt } from '../components/archive/SelfPersonPrompt';
import { treeHref, personHref } from '../utils/prettyArchiveLinks';
import {
  buildTreePeopleAddPersonUrl,
  pickReadinessTree,
  setLastOpenedTreeId,
} from '../utils/archiveNavigation';
import { ADD_CONNECTION } from '../utils/connectionCopy';
import { AddToHomeScreenCard } from '../components/roadmap/AddToHomeScreenCard';
import { LegacyBookExportTeaser } from '../components/roadmap/LegacyBookExportTeaser';
import { ChooseWorkspaceSection } from '../components/dashboard/ChooseWorkspaceSection';
import { CoreJourneySection } from '../components/dashboard/CoreJourneySection';
import { DashboardContinueArchiveSection } from '../components/dashboard/DashboardContinueArchiveSection';
import { DashboardSupportGuidanceSection } from '../components/dashboard/DashboardSupportGuidanceSection';
import { DashboardFounderLinks } from '../components/dashboard/DashboardFounderLinks';
import { FamilyGatheringRoomCard } from '../components/gathering';
import { TESTER_SIMPLIFIED_UI, DNA_UPLOAD_VISIBLE, CORE_JOURNEY_UI } from '../config/testerRelease';
import { NotificationBell } from '../components/reminders/NotificationBell';

const API = getApiBase();

const Dashboard = () => {
  const { user, logout, refreshUser, authReady } = useAuth();
  const {
    needsWelcome,
    needsFirstTreeHint,
    markFirstTreeCreated,
    markFirstPersonAdded,
    markFirstStoryRecorded,
    isOnboardingFullyDone,
    dashboardChecklistMinimized,
    dashboardChecklistDismissed,
    setDashboardChecklistMinimized,
    reopenDashboardChecklist,
    walkthroughReplayNonce,
  } = useOnboarding();
  const [trees, setTrees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [treeName, setTreeName] = useState('');
  const [treeDescription, setTreeDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [treeUsage, setTreeUsage] = useState(null);
  const [showLimitDialog, setShowLimitDialog] = useState(false);
  const [myArchivePersonId, setMyArchivePersonId] = useState(() => getMyArchivePersonId());
  const [selfPromptDismissed, setSelfPromptDismissed] = useState(() => Boolean(getSelfPromptDismissed()));
  const navigate = useNavigate();
  const readinessTree = useMemo(() => pickReadinessTree(trees), [trees]);

  const openTree = (tree) => {
    if (!tree) return;
    setLastOpenedTreeId(tree.id);
    navigate(treeHref(tree));
  };

  useEffect(() => {
    const sync = () => setMyArchivePersonId(getMyArchivePersonId());
    window.addEventListener('storage', sync);
    window.addEventListener('gb-my-archive-person-updated', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('gb-my-archive-person-updated', sync);
    };
  }, []);

  useEffect(() => {
    if (!authReady) return;
    fetchTrees();
    fetchTreeUsage();
  }, [authReady]);

  const fetchTreeUsage = async () => {
    try {
      const { data } = await axios.get(`${API}/billing/me`);
      setTreeUsage({
        plan: data.current_plan || 'free',
        limit: data.tree_limit ?? 1,
        owned: data.owned_tree_count ?? 0,
        canCreate: data.can_create_tree !== false,
        canUpgradeTo: data.can_upgrade_to || [],
      });
    } catch {
      /* billing optional — fall back to local count */
    }
  };

  const ownedTreeCount = trees.filter((t) => t.role === 'owner').length;
  const effectiveLimit = treeUsage?.limit ?? 1;
  const effectiveOwned = treeUsage?.owned ?? ownedTreeCount;
  const canCreateTree = treeUsage ? treeUsage.canCreate : ownedTreeCount < effectiveLimit;
  const usageLabel = getTreeUsageLabel(effectiveOwned, effectiveLimit);

  const openCreateTreeFlow = () => {
    if (!canCreateTree) {
      setShowLimitDialog(true);
      return;
    }
    setIsCreateDialogOpen(true);
  };

  // Align local onboarding flags with server state (e.g. new device or cleared storage)
  useEffect(() => {
    if (loading || trees.length === 0) return;
    markFirstTreeCreated();
    let cancelled = false;
    (async () => {
      try {
        const { data: people } = await axios.get(`${API}/individuals/${trees[0].id}`);
        if (cancelled) return;
        if (people?.length >= 1) {
          markFirstPersonAdded();
          const { data: storyList } = await axios.get(`${API}/individuals/${people[0].id}/stories`);
          if (!cancelled && storyList?.length >= 1) {
            markFirstStoryRecorded();
          }
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loading, trees, markFirstTreeCreated, markFirstPersonAdded, markFirstStoryRecorded]);

  /** Resolve “my” person in the archive: explicit localStorage link, else cautious name match across trees. */
  useEffect(() => {
    if (loading || trees.length === 0) return;
    let cancelled = false;
    (async () => {
      const stored = getMyArchivePersonId();
      const flat = [];
      try {
        await Promise.all(
          trees.map(async (t) => {
            try {
              const { data } = await axios.get(`${API}/individuals/${t.id}`);
              const rows = Array.isArray(data) ? data : [];
              for (const row of rows) {
                flat.push({ ...row, tree_id: row.tree_id || t.id });
              }
            } catch {
              /* ignore per-tree */
            }
          })
        );
      } catch {
        /* ignore */
      }
      if (cancelled) return;
      const guess = findNameMatchMyPerson(user?.name, flat);
      if (flat.length > 0) {
        if (stored && flat.some((p) => p.id === stored)) {
          setMyArchivePersonId(stored);
        } else {
          if (stored) clearPersistedArchivePersonIfStale(null);
          setMyArchivePersonId(guess?.personId || null);
        }
      } else if (stored) {
        setMyArchivePersonId(stored);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loading, trees, user?.name]);

  const myProfileHref = myArchivePersonId ? `${personHref({ id: myArchivePersonId })}?view=stories` : null;
  const myProfileFallbackTree = trees[0];

  const showDashboardChecklistExpanded =
    !dashboardChecklistDismissed && !dashboardChecklistMinimized;

  // Show welcome modal after trees are loaded (to check empty state)
  useEffect(() => {
    if (!loading && needsWelcome) {
      const timer = setTimeout(() => setShowWelcome(true), 300);
      return () => clearTimeout(timer);
    }
  }, [loading, needsWelcome]);

  useEffect(() => {
    if (walkthroughReplayNonce > 0) {
      setShowWelcome(true);
    }
  }, [walkthroughReplayNonce]);

  const fetchTrees = async () => {
    try {
      const response = await axios.get(`${API}/trees`);
      setTrees(response.data);
      setTreeUsage((prev) => {
        const owned = (response.data || []).filter((t) => t.role === 'owner').length;
        if (prev) {
          return { ...prev, owned, canCreate: owned < (prev.limit ?? 1) };
        }
        return prev;
      });
    } catch (error) {
      toast.error(humanizeError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTree = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const response = await axios.post(`${API}/trees`, {
        name: treeName,
        description: treeDescription
      });
      toast.success('Tree created successfully!');
      setTrees([...trees, response.data]);
      setIsCreateDialogOpen(false);
      setTreeName('');
      setTreeDescription('');
      await fetchTreeUsage();
      
      // Mark first tree created for onboarding
      markFirstTreeCreated();
      
      // Navigate to the new tree
      navigate(treeHref(response.data));
    } catch (error) {
      if (error?.response?.status === 403) {
        setIsCreateDialogOpen(false);
        await fetchTreeUsage();
        setShowLimitDialog(true);
      } else {
        toast.error(humanizeError(error));
      }
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteTree = async (treeId, treeName) => {
    if (!window.confirm(`Are you sure you want to delete "${treeName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/trees/${treeId}`);
      toast.success('Tree deleted successfully');
      setTrees(trees.filter(t => t.id !== treeId));
      await fetchTreeUsage();
    } catch (error) {
      toast.error(humanizeError(error));
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      owner: 'border border-[#c9a961]/30 bg-[#c9a961]/12 text-[#5c4a32]',
      editor: 'border border-[#e8e2d8] bg-[#ebe6dc]/70 text-[#4a433c]',
      viewer: 'border border-[#e8e2d8]/80 bg-[#faf8f5] text-[#68645e]',
    };
    return styles[role] || styles.viewer;
  };

  const getRoleLabel = (role) => getTreeCollaborationRoleLabel(role);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <LoadingState message="Loading your family trees..." />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* Welcome Modal for first-time users */}
      <GuideWelcomeModal
        isOpen={showWelcome}
        onClose={() => setShowWelcome(false)}
        onExplore={() => setDashboardChecklistMinimized(true)}
        onBeginSetup={() => {
          setShowWelcome(false);
          reopenDashboardChecklist();
          if (trees.length === 0) openCreateTreeFlow();
        }}
      />
      
      <nav className="border-b border-[hsl(38_16%_80%)] bg-[hsl(42_28%_96%_/_0.97)] backdrop-blur-sm">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <Link
            to="/home"
            className="flex items-center gap-2.5 text-[#1c1917] transition hover:opacity-85"
            data-testid="dashboard-logo"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#c9a961]/20 text-xs font-semibold text-[#5c4a32] ring-1 ring-[#c9a961]/25 sm:h-9 sm:w-9 sm:text-sm">
              G
            </span>
            <span className="hidden min-[380px]:inline">
              <BrandWordmark variant="navApp" className="text-lg sm:text-xl" />
            </span>
          </Link>
          <div className="flex items-center gap-1 sm:gap-2">
            <NotificationBell />
            {!CORE_JOURNEY_UI && !TESTER_SIMPLIFIED_UI && (
            <Link to="/gene-engine">
              <Button
                variant="ghost"
                size="sm"
                className="hidden items-center gap-2 text-[#5c534a] hover:bg-[#ebe6dc]/60 hover:text-[#1c1917] sm:flex"
                data-testid="gene-engine-btn"
              >
                <Dna className="h-4 w-4" />
                <span className="text-sm">Gene Engine</span>
              </Button>
              <Button variant="ghost" size="icon" className="sm:hidden text-[#5c534a]" data-testid="gene-engine-btn-mobile">
                <Dna className="h-5 w-5" />
              </Button>
            </Link>
            )}
            {!CORE_JOURNEY_UI && DNA_UPLOAD_VISIBLE && (
            <Link to="/dna">
              <Button
                variant="ghost"
                size="sm"
                className="hidden items-center gap-2 text-[#5c534a] hover:bg-[#ebe6dc]/60 hover:text-[#1c1917] sm:flex"
                data-testid="dna-management-btn"
              >
                <Dna className="h-4 w-4" aria-hidden />
                <span className="text-sm">DNA</span>
                {TESTER_SIMPLIFIED_UI ? (
                  <Badge variant="secondary" className="px-1.5 py-0 text-[10px] font-medium uppercase tracking-wide">
                    Beta
                  </Badge>
                ) : null}
              </Button>
              <Button variant="ghost" size="icon" className="relative sm:hidden text-[#5c534a]" data-testid="dna-management-btn-mobile">
                <Dna className="h-5 w-5" />
                {TESTER_SIMPLIFIED_UI ? (
                  <span className="absolute -right-0.5 -top-0.5 rounded-full bg-[#c9a961] px-1 text-[8px] font-semibold uppercase text-white">
                    β
                  </span>
                ) : null}
              </Button>
            </Link>
            )}
            {!CORE_JOURNEY_UI && (
            <Link to="/family-spaces">
              <Button
                variant="ghost"
                size="sm"
                className="hidden items-center gap-2 text-[#5c534a] hover:bg-[#ebe6dc]/60 hover:text-[#1c1917] sm:flex"
                data-testid="family-spaces-btn"
              >
                <Users className="h-4 w-4" />
                <span className="text-sm">Family Spaces</span>
              </Button>
            </Link>
            )}
            <GuideLauncher />
            <Link
              to="/guide"
              className="hidden items-center gap-1.5 text-sm text-[#5c534a] transition hover:text-[#1c1917] sm:flex"
              data-testid="guide-link"
            >
              <span>Guide</span>
            </Link>
            <Link
              to="/faq"
              className="hidden items-center gap-1.5 text-sm text-[#5c534a] transition hover:text-[#1c1917] sm:flex"
              data-testid="faq-link"
            >
              <HelpCircle className="h-4 w-4 opacity-80" />
              <span>FAQ</span>
            </Link>
            <Link
              to="/help"
              className="hidden items-center gap-1.5 text-sm text-[#5c534a] transition hover:text-[#1c1917] sm:flex"
              data-testid="help-guides-link"
            >
              <span>Help &amp; Guides</span>
            </Link>
            {!isOnboardingFullyDone && dashboardChecklistDismissed && (
              <button
                type="button"
                onClick={() => reopenDashboardChecklist()}
                className="text-xs font-medium text-[#7a7268] underline decoration-[#c9a961]/40 underline-offset-4 transition hover:text-[#1c1917]"
                data-testid="dashboard-setup-progress-nav"
              >
                <span className="sm:hidden">Setup</span>
                <span className="hidden sm:inline">Setup progress</span>
              </button>
            )}
            {myProfileHref ? (
              <Link
                to={myProfileHref}
                className="flex items-center gap-1.5 rounded-full border border-[#e8e2d8]/90 bg-[#fffcf7]/80 px-2 py-1.5 text-[#5c4a32] transition hover:border-[#c9a961]/45 hover:bg-[#faf8f5] sm:px-2.5 sm:text-xs sm:font-medium"
                title="Open your archive profile — stories and documents"
                data-testid="my-archive-profile-link"
              >
                <User className="h-4 w-4 shrink-0 opacity-80 sm:h-3.5 sm:w-3.5" aria-hidden />
                <span className="hidden sm:inline">My profile</span>
              </Link>
            ) : (
              myProfileFallbackTree && (
                <Link
                  to={`${treeHref(myProfileFallbackTree)}?tab=grid`}
                  className="flex items-center gap-1.5 text-xs font-medium text-[#7a7268] underline decoration-[#c9a961]/35 underline-offset-4 transition hover:text-[#1c1917]"
                  title="Open People, then use “This is me” on your profile to link your account"
                  data-testid="my-archive-profile-hint"
                >
                  <User className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
                  <span className="hidden min-[400px]:inline">Find me</span>
                </Link>
              )
            )}
            <span className="hidden max-w-[10rem] truncate text-sm text-[#68645e] md:inline-block" data-testid="user-name">
              {user?.name}
            </span>
            <Link to="/settings">
              <Button variant="ghost" size="icon" className="text-[#5c534a] hover:bg-[#ebe6dc]/60" data-testid="settings-btn">
                <SettingsIcon className="h-5 w-5" />
              </Button>
            </Link>
            <Button variant="ghost" size="icon" className="text-[#5c534a] hover:bg-[#ebe6dc]/60" onClick={logout} data-testid="logout-btn">
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </nav>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-10 lg:px-8 lg:py-12">
        <div className="lg:grid lg:grid-cols-12 lg:items-start lg:gap-x-8">
          {!isOnboardingFullyDone && dashboardChecklistMinimized && !dashboardChecklistDismissed && (
            <div className="mb-5 lg:col-span-12">
              <button
                type="button"
                onClick={() => setDashboardChecklistMinimized(false)}
                className="flex w-full items-center justify-between gap-3 rounded-2xl border border-[hsl(38_16%_86%)] bg-[hsl(42_26%_99%)] px-4 py-3 text-left shadow-sm transition-colors hover:border-[hsl(38_14%_78%)] hover:bg-[hsl(42_24%_98%)]"
                data-testid="dashboard-checklist-restore"
              >
                <span className="font-serif text-[15px] text-[hsl(24_14%_18%)]">Build your archive</span>
                <span className="flex items-center gap-2 text-[13px] text-[hsl(32_12%_42%)]">
                  Show steps
                  <ChevronDown className="h-4 w-4 opacity-70" aria-hidden />
                </span>
              </button>
            </div>
          )}

          <div
            className={`min-w-0 space-y-8 ${showDashboardChecklistExpanded ? 'lg:col-span-8' : 'lg:col-span-12'}`}
          >
            {/* Primary header + New tree — first */}
            <section
              className="rounded-2xl border border-[hsl(38_16%_82%)]/80 bg-gradient-to-br from-[hsl(42_30%_100%)] to-[hsl(40_22%_97%)] px-5 py-6 shadow-[0_8px_32px_-20px_hsl(28_14%_18%_/_0.12)] sm:px-7 sm:py-7"
              data-testid="dashboard-primary-header"
            >
              <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 space-y-2">
                  <h2
                    className="font-serif text-3xl font-light tracking-tight text-[#1c1917] sm:text-[2rem]"
                    data-testid="dashboard-title"
                  >
                    Your archives
                  </h2>
                  <p className="max-w-xl text-[0.95rem] leading-relaxed text-[#5c534a]">
                    {CORE_JOURNEY_UI
                      ? 'Your private place to add loved ones, save memories, and create a Legacy Book for your family.'
                      : 'Open a family archive to add people, stories, and documents — or start a new one when you&apos;re ready.'}
                  </p>
                  <p
                    className="text-xs font-medium tabular-nums text-[#7a7268]"
                    data-testid="tree-usage-label"
                  >
                    {usageLabel}
                  </p>
                  {myProfileHref && (
                    <p className="text-xs text-[#7a7268]">
                      Signed in as <span className="font-medium text-[#5c534a]">{user?.name}</span>
                      {' · '}
                      <Link to={myProfileHref} className="text-[#5c4a32] underline decoration-[#c9a961]/40 underline-offset-2">
                        My archive profile
                      </Link>
                    </p>
                  )}
                </div>

                <div className="flex shrink-0 flex-col items-stretch gap-2 sm:items-end">
                  <Button
                    type="button"
                    onClick={openCreateTreeFlow}
                    className="rounded-full bg-primary px-7 py-2.5 text-base text-primary-foreground shadow-[0_4px_14px_-4px_hsl(var(--primary)/0.45)] hover:bg-[hsl(152_28%_24%)]"
                    data-testid="create-tree-btn"
                  >
                    <Plus className="mr-2 h-5 w-5" /> New archive
                  </Button>
                  {!canCreateTree ? (
                    <p className="max-w-[14rem] text-center text-[11px] leading-snug text-muted-foreground sm:text-right">
                      {getTreeLimitUpgradeMessage(treeUsage?.plan || 'free', { atLimit: true })}
                    </p>
                  ) : null}
                </div>
              </div>
            </section>

            {CORE_JOURNEY_UI ? (
              <CoreJourneySection
                firstTree={trees[0] || null}
                myArchivePersonId={myArchivePersonId}
                onCreateArchive={openCreateTreeFlow}
              />
            ) : (
              <ChooseWorkspaceSection />
            )}

            <DashboardContinueArchiveSection
              firstTree={trees[0] || null}
              myArchivePersonId={myArchivePersonId}
              onCreateArchive={openCreateTreeFlow}
            />

            {!CORE_JOURNEY_UI && <DashboardSupportGuidanceSection />}

            {!CORE_JOURNEY_UI && <FamilyGatheringRoomCard />}

            <DashboardFounderLinks />

            {/* Self-person onboarding prompt — shown once when user has trees but no self-link */}
            {trees.length > 0 && !user?.self_person_id && !selfPromptDismissed && (
              <SelfPersonPrompt
                trees={trees}
                onLinked={(personId, treeId) => {
                  setMyArchivePersonId(personId);
                  setSelfPromptDismissed(true);
                  refreshUser();
                }}
                onDismiss={(reason) => {
                  setSelfPromptDismissed(true);
                }}
              />
            )}

            {/* Existing trees — before secondary panels */}
            {trees.length === 0 ? (
              <div
                className="rounded-[1.75rem] border border-dashed border-[#c9a961]/35 bg-[#fffcf7]/60 px-6 py-12 sm:px-10 sm:py-14"
                data-testid="empty-state"
              >
                {needsFirstTreeHint && (
                  <ArchiveHelperText className="mx-auto mb-6 max-w-lg text-center text-[#5c534a]">
                    Give your tree a calm name—perhaps a surname or &quot;My ancestors&quot;—then add people whenever
                    the moment feels right.
                  </ArchiveHelperText>
                )}
                <EmptyState
                  icon={GitBranch}
                  title="Your archives will live here"
                  description="When you're ready, create a private family archive for the people, stories, and keepsakes you want to preserve."
                  action={
                    <Button
                      onClick={openCreateTreeFlow}
                      className="rounded-full bg-primary px-8 text-primary-foreground shadow-[0_4px_14px_-4px_hsl(var(--primary)/0.45)] hover:bg-[hsl(152_28%_24%)]"
                      data-testid="empty-create-tree-btn"
                    >
                      <Plus className="mr-2 h-5 w-5" /> Create your first archive
                    </Button>
                  }
                  variant="narrative"
                  className="py-0"
                />
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-5 sm:gap-6 md:grid-cols-2 md:gap-6 xl:grid-cols-3" data-testid="trees-grid">
                {trees.map((tree) => (
                  <Card
                    key={tree.id}
                    className="group overflow-hidden rounded-2xl border border-[hsl(38_16%_78%)] bg-card shadow-[0_4px_20px_-12px_hsl(28_14%_18%_/_0.12)] transition-all duration-300 hover:border-[hsl(43_48%_58%_/_0.4)] hover:shadow-[0_12px_32px_-16px_hsl(28_14%_18%_/_0.16)]"
                    data-testid={`tree-card-${tree.id}`}
                  >
                    <button
                      type="button"
                      className="block w-full cursor-pointer border-0 bg-transparent p-0 text-left"
                      onClick={() => openTree(tree)}
                      aria-label={`Open ${tree.name}`}
                    >
                      <ArchiveTreeCover
                        coverImageUrl={tree.cover_image_url}
                        coverDisplay={tree.cover_display}
                        alt={`${tree.name} archive cover`}
                        variant="card"
                      />
                    </button>
                    <CardHeader className="pb-3 pt-4">
                      <div className="cursor-pointer" onClick={() => openTree(tree)}>
                        {/* Archive type label */}
                        <p className="mb-1.5 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8b7355]">
                          <span
                            className="inline-block h-1.5 w-1.5 rounded-full bg-primary/50"
                            aria-hidden
                          />
                          {tree.archive_type === 'memorial'
                            ? 'Memorial Archive'
                            : tree.archive_type === 'organisation'
                            ? 'Organisation Archive'
                            : 'Family Archive'}
                        </p>
                        <CardTitle
                          className="mb-2 font-serif text-xl leading-tight text-[#1c1917]"
                          data-testid={`tree-title-${tree.id}`}
                        >
                          {tree.name}
                        </CardTitle>
                        <CardDescription className="text-sm leading-relaxed text-[#5c534a]">
                          {tree.description || 'Stories, people, and documents in one place'}
                        </CardDescription>
                      </div>
                    </CardHeader>

                    <CardContent className="pt-0">
                      <div className="flex flex-col gap-3">
                        <button
                          onClick={() => openTree(tree)}
                          className="flex items-center gap-2 text-sm font-medium text-[#1c1917] underline decoration-[#c9a961]/45 underline-offset-4 transition hover:text-[#5c4a32]"
                          data-testid={`view-tree-btn-${tree.id}`}
                        >
                          <GitBranch className="h-4 w-4 text-[#8b7355]" />
                          Open archive
                        </button>

                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-[#68645e]">
                          <button
                            type="button"
                            onClick={() => {
                              setLastOpenedTreeId(tree.id);
                              navigate(`${treeHref(tree)}?tab=readiness`);
                            }}
                            className="flex items-center gap-1.5 transition hover:text-[#1c1917]"
                            data-testid={`tree-readiness-btn-${tree.id}`}
                          >
                            <ClipboardCheck className="h-3.5 w-3.5" />
                            {CORE_JOURNEY_UI ? 'Legacy Book' : 'Keepsake readiness'}
                          </button>
                          {(tree.role === 'owner' || tree.role === 'editor') && (
                            <button
                              type="button"
                              onClick={() => {
                                setLastOpenedTreeId(tree.id);
                                navigate(buildTreePeopleAddPersonUrl(tree));
                              }}
                              className="flex items-center gap-1.5 font-medium text-[#5c4a32] transition hover:text-[#1c1917]"
                              data-testid={`tree-add-connection-btn-${tree.id}`}
                            >
                              <Link2 className="h-3.5 w-3.5" />
                              {ADD_CONNECTION}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              setLastOpenedTreeId(tree.id);
                              navigate(`${treeHref(tree)}?tab=timeline`);
                            }}
                            className="flex items-center gap-1.5 transition hover:text-[#1c1917]"
                            data-testid={`view-timeline-btn-${tree.id}`}
                          >
                            <Clock className="h-3.5 w-3.5" />
                            Timeline
                          </button>
                          {tree.role === 'owner' && (
                            <button
                              type="button"
                              onClick={() => {
                                setLastOpenedTreeId(tree.id);
                                navigate(`${treeHref(tree)}/members`);
                              }}
                              className="flex items-center gap-1.5 transition hover:text-[#1c1917]"
                              data-testid={`manage-members-btn-${tree.id}`}
                            >
                              <Users className="h-3.5 w-3.5" />
                              Members
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="mt-6 flex items-center justify-between border-t border-[#e8e2d8]/80 pt-4">
                        <Badge variant="secondary" className={`${getRoleBadge(tree.role)} text-xs font-normal`} data-testid={`tree-role-${tree.id}`}>
                          {getRoleLabel(tree.role)}
                        </Badge>

                        <div className="flex items-center gap-3">
                          {tree.member_count > 0 && (
                            <span className="text-xs text-[#8b7355]" data-testid={`member-count-${tree.id}`}>
                              {tree.member_count} {tree.member_count === 1 ? 'collaborator' : 'collaborators'}
                            </span>
                          )}

                          {tree.role === 'owner' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteTree(tree.id, tree.name);
                              }}
                              data-testid={`delete-tree-btn-${tree.id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive transition-colors" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {readinessTree && !CORE_JOURNEY_UI ? (
              <Link
                to={`${treeHref(readinessTree)}?tab=readiness`}
                onClick={() => setLastOpenedTreeId(readinessTree.id)}
                className="group block rounded-2xl border border-[#e8e2d8]/90 bg-[#fffcf7] p-4 transition hover:border-[#c9a961]/35 hover:shadow-sm sm:p-5"
                data-testid="dashboard-keepsake-readiness-link"
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex min-w-0 items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#c9a961]/15 text-[#8b7355]">
                      <ClipboardCheck className="h-5 w-5" aria-hidden />
                    </span>
                    <div className="min-w-0">
                      <p className="text-[10px] font-medium uppercase tracking-[0.14em] text-[#8b7355]">
                        Export &amp; progress
                      </p>
                      <p className="font-serif text-base text-[#1c1917]">Keepsake readiness</p>
                      <p className="mt-0.5 text-sm text-[#5c534a]">
                        Book progress and export status for {readinessTree.name}
                        {trees.length > 1 ? (
                          <span className="text-[#7a7268]"> · also on each archive card above</span>
                        ) : null}
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 shrink-0 text-[#8b7355] opacity-60 transition group-hover:opacity-100" aria-hidden />
                </div>
              </Link>
            ) : null}

            {/* Support / promotional — lower on the page */}
            <div className="grid gap-4 md:grid-cols-2" data-testid="dashboard-roadmap-strip">
              <AddToHomeScreenCard />
              {!CORE_JOURNEY_UI && <LegacyBookExportTeaser variant="dashboard" />}
            </div>

            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogContent data-testid="create-tree-dialog" className="sm:max-w-md border-[#e8e2d8] bg-[#fffcf7]">
                <DialogHeader className="space-y-2">
                  <DialogTitle className="font-serif text-xl font-normal text-[#1c1917]">Create a new family archive</DialogTitle>
                  <DialogDescription className="text-[0.95rem] leading-relaxed text-[#5c534a]">
                    A private space for people, stories, documents, and memories you want to preserve together.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateTree}>
                  <div className="space-y-5 py-4">
                    {/* Archive type — Family Archive is the only active type; others are placeholders */}
                    <div className="flex items-center gap-2 rounded-lg border border-[#e8e2d8]/70 bg-[hsl(42_24%_97%)] px-3 py-2.5">
                      <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary/60" aria-hidden />
                      <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#8b7355]">
                        Family Archive
                      </span>
                      <span className="ml-auto text-[11px] text-muted-foreground/60">
                        More archive types planned
                      </span>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="tree-name" className="text-sm font-medium text-[#3d3833]">
                        Archive name
                      </Label>
                      <Input
                        id="tree-name"
                        placeholder="e.g., The Williams Family Archive"
                        value={treeName}
                        onChange={(e) => setTreeName(e.target.value)}
                        required
                        className="border-[#e8e2d8] bg-white text-base"
                        data-testid="tree-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="tree-description" className="text-sm font-medium text-[#68645e]">
                        Note (optional)
                      </Label>
                      <Textarea
                        id="tree-description"
                        placeholder="A few words about this archive…"
                        value={treeDescription}
                        onChange={(e) => setTreeDescription(e.target.value)}
                        rows={2}
                        className="resize-none border-[#e8e2d8] bg-white"
                        data-testid="tree-description-input"
                      />
                    </div>
                  </div>
                  <DialogFooter className="sm:justify-end">
                    <Button
                      type="submit"
                      disabled={creating || !treeName.trim()}
                      className="rounded-full bg-primary px-6 text-primary-foreground hover:bg-[hsl(152_28%_24%)]"
                      data-testid="create-tree-submit-btn"
                    >
                      {creating ? 'Creating…' : 'Create archive'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>

            <Dialog open={showLimitDialog} onOpenChange={setShowLimitDialog}>
              <DialogContent className="sm:max-w-md border-[#e8e2d8] bg-[#fffcf7]" data-testid="tree-limit-dialog">
                <DialogHeader>
                  <DialogTitle className="font-serif text-xl font-normal text-[#1c1917]">
                    Archive limit reached
                  </DialogTitle>
                  <DialogDescription className="text-[0.95rem] leading-relaxed text-[#5c534a]">
                    {getTreeLimitUpgradeMessage(treeUsage?.plan || 'free', { atLimit: true })}
                  </DialogDescription>
                </DialogHeader>
                <p className="text-sm text-muted-foreground tabular-nums">{usageLabel}</p>
                <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
                  <Button type="button" variant="outline" className="rounded-full" onClick={() => setShowLimitDialog(false)}>
                    Not now
                  </Button>
                  {(treeUsage?.canUpgradeTo?.length ?? 0) > 0 ? (
                    <Button asChild className="rounded-full bg-primary text-primary-foreground">
                      <Link to="/pricing">View plans</Link>
                    </Button>
                  ) : null}
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {showDashboardChecklistExpanded && (
            <aside className="mt-8 w-full max-w-md shrink-0 lg:sticky lg:top-6 lg:mt-0 lg:max-w-none lg:col-span-4">
              <BuildArchiveChecklist
                user={user}
                trees={trees}
                onOpenCreateTree={openCreateTreeFlow}
                onOpenExport={(tree) => {
                  markGuideExportPreviewed();
                  setLastOpenedTreeId(tree.id);
                  navigate(`${treeHref(tree)}?tab=settings`);
                }}
                className="w-full"
              />
            </aside>
          )}
        </div>
      </div>

      <AppFooter variant="light" />
    </div>
  );
};

export default Dashboard;